import React from 'react';
import { Tooltip } from 'antd';

import type {
  AccessLegs,
  LaneMode,
  NormalizedQuote,
  TruckingLeg,
} from '../../../apis/lanePlanner';
import CarrierChip from './CarrierChip';
import QuoteDetails from './QuoteDetails';
import RiskMetric from './RiskMetric';
import {
  carrierNameOf,
  carrierOf,
  departureParts,
  formatMoney,
  legLogoOf,
  riskPercentOf,
  routeOf,
  scoreModeOf,
  pickupLabelOf,
  serviceLevelOf,
  stopsLabel,
  travelOf,
} from './quoteView';
import styles from './styles.module.css';
import {
  truckingLegsOf,
  type TruckingChoices,
  type TruckingLegView,
} from './truckingView';

interface QuoteCardProps {
  quote: NormalizedQuote;
  vendorName: string;
  expanded: boolean;
  onToggle: () => void;
  /** Transit exceeds the stability budget of the chosen packaging. */
  overBudget?: boolean;
  /** Saving is offered only where a quote can be saved (search results). */
  saved?: boolean;
  saving?: boolean;
  onSave?: () => void;
  onShare: () => void;
  /** Extra buttons in the action row, for example Remove on the Saved tab. */
  extraActions?: React.ReactNode;
  /** Present only for quotes the vendor lets us book. */
  onBook?: () => void;
  /** The search's ship date: the pickup day shown for road quotes, which carry no departure. */
  shipDate?: string;
  /** Search results hide a road quote's lane (the whole search is that one lane); the Saved tab mixes searches and keeps it. */
  hideRoadLane?: boolean;
  /** The search's trucking per airport or port, for the trucking legs of an air or ocean quote. */
  accessLegs?: AccessLegs;
  /** The trucking option picked per leg and site. */
  truckingChoices?: TruckingChoices;
  /** Opens the choice of another trucking carrier for a leg. */
  onChangeTrucking?: (leg: TruckingLeg, site: string) => void;
}

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    width='12'
    height='12'
    viewBox='0 0 12 12'
    fill='none'
    aria-hidden
    className={open ? styles.chevronOpen : undefined}
  >
    <path
      d='M2.5 4.5 6 8l3.5-3.5'
      stroke='currentColor'
      strokeWidth='1.6'
      fill='none'
    />
  </svg>
);

interface Chip {
  mode: LaneMode;
  carrier: string | null;
  logoUrl: string | null;
}

/** The carriers on the card, the quote's own first (it is the main leg), then the pickup and delivery trucks. */
const chipsOf = (
  quote: NormalizedQuote,
  trucking: TruckingLegView[]
): Chip[] => {
  const quoteCarrier = carrierNameOf(quote);
  const own: Chip[] = quote.legs.length
    ? quote.legs.map((leg) => {
        // a leg the vendor left unnamed takes the quote's carrier
        const named = { ...leg, carrier: leg.carrier ?? quoteCarrier };
        return {
          mode: leg.mode,
          carrier: named.carrier,
          logoUrl: legLogoOf(named),
        };
      })
    : [{ mode: quote.mode, carrier: quoteCarrier, logoUrl: null }];
  const trucks = trucking
    .filter((view) => view.carrier)
    .map((view) => ({
      mode: 'road' as const,
      carrier: view.carrier,
      logoUrl: view.logoUrl,
    }));
  const seen = new Set<string>();
  return [...own, ...trucks].filter((chip) => {
    // one chip per named carrier, whichever logo each leg resolved; the logo tells unnamed ones apart
    const key = `${chip.mode}|${chip.carrier?.toLowerCase() ?? chip.logoUrl}`;
    return !seen.has(key) && !!seen.add(key);
  });
};

const QuoteCard: React.FC<QuoteCardProps> = ({
  quote,
  vendorName,
  expanded,
  onToggle,
  overBudget,
  saved,
  saving,
  onSave,
  onShare,
  extraActions,
  onBook,
  shipDate,
  hideRoadLane,
  accessLegs,
  truckingChoices,
  onChangeTrucking,
}) => {
  const carrier = carrierOf(quote);
  const chips = chipsOf(
    quote,
    truckingLegsOf(quote, accessLegs, truckingChoices, shipDate)
  );
  const departure = departureParts(quote);
  const route = hideRoadLane && quote.mode === 'road' ? null : routeOf(quote);
  const travel = travelOf(quote);
  const stops = stopsLabel(quote);
  const serviceLevel = serviceLevelOf(quote);
  const pickup = pickupLabelOf(quote, shipDate);

  return (
    <div
      className={`${styles.rateCardShell} ${
        overBudget ? styles.rateCardShellOverBudget : ''
      }`}
    >
      <div
        className={`${styles.quoteRow} ${expanded ? styles.quoteRowOpen : ''}`}
        onClick={onToggle}
      >
        <div className={styles.rateCarrierCell}>
          <div className={styles.cardChips}>
            {chips.map((chip) => (
              <CarrierChip
                key={`${chip.mode}-${chip.carrier}-${chip.logoUrl}`}
                className={styles.cardChip}
                glyphSize={11}
                {...chip}
              />
            ))}
          </div>
          <div className={styles.rateCarrierText}>
            <div className={styles.rateName} title={carrier}>
              {carrier}
            </div>
            {serviceLevel && (
              <div className={styles.rateCellSub}>{serviceLevel}</div>
            )}
            {route && <div className={styles.rateCellSub}>{route}</div>}
            {overBudget && (
              <div className={styles.overBudgetStatus} role='status'>
                Over stability budget
              </div>
            )}
          </div>
        </div>
        <div>
          {departure ? (
            <>
              <div className={styles.rateCellValue}>{departure.time}</div>
              <div className={styles.rateCellSub}>{departure.date}</div>
            </>
          ) : pickup ? (
            <>
              <div className={styles.rateCellValue}>Pickup</div>
              <div className={styles.rateCellSub}>{pickup}</div>
            </>
          ) : (
            <div className={styles.rateCellSub}>Schedule not provided</div>
          )}
        </div>
        <div>
          <div className={styles.rateCellValue}>{travel?.label ?? '—'}</div>
          {stops && <div className={styles.rateCellSub}>{stops}</div>}
          {travel?.note && (
            <div className={styles.rateCellSub}>{travel.note}</div>
          )}
        </div>
        <RiskMetric percent={riskPercentOf(quote)} mode={scoreModeOf(quote)} />
        <div className={styles.ratePriceCell}>
          <div className={styles.ratePrice}>
            {quote.price == null
              ? 'Price on request'
              : formatMoney(quote.price, quote.currency)}
          </div>
          <Tooltip
            title={
              quote.rate_type === 'tariff'
                ? 'A price-list rate for this lane, not a firm offer. It cannot be booked here.'
                : quote.bookable
                  ? 'An estimate from the vendor; the final price is confirmed when booked.'
                  : 'An estimate from the vendor. It cannot be booked here for this account.'
            }
          >
            <div className={styles.rateCurrency} tabIndex={0}>
              {quote.rate_type === 'tariff' ? 'Indicative rate' : 'Estimated'}
            </div>
          </Tooltip>
        </div>
        <div className={styles.chevronCell}>
          <button
            type='button'
            className={styles.chevronButton}
            aria-expanded={expanded}
            aria-label={`${[carrier, serviceLevel].filter(Boolean).join(', ')} quote`}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            <Chevron open={expanded} />
          </button>
        </div>
      </div>
      {expanded && (
        <div className={styles.quotePanel}>
          <QuoteDetails
            quote={quote}
            vendorName={vendorName}
            shipDate={shipDate}
            accessLegs={accessLegs}
            choices={truckingChoices}
            onChangeTrucking={onChangeTrucking}
          />
          <div className={styles.quoteActions}>
            <button
              type='button'
              className={styles.shareLink}
              onClick={onShare}
            >
              Share
            </button>
            {extraActions}
            {onSave && (
              <button
                type='button'
                className={styles.secondaryAction}
                disabled={saved || saving}
                onClick={onSave}
              >
                {saved ? 'Saved' : saving ? 'Saving…' : 'Save quote'}
              </button>
            )}
            {onBook && (
              <button
                type='button'
                className={styles.bookButton}
                aria-label={`Book ${carrier}`}
                onClick={onBook}
              >
                Book
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteCard;
